/**
 * 타임라인 렌더러 (Phase 1-2) — **읽기 전용**.
 *
 * 좌표 계산은 전부 `timeline-model.js`(순수 함수)가 한다. 이 파일은 그 결과를
 * DOM으로 그리기만 한다.
 *
 * ## 이 모듈이 지키는 제약 (설계 §1-5·§1-6, 감사 B-4)
 *
 * 1. **모델을 수정하지 않는다.** 기존 `renderNoteListImmediate`는 렌더 중
 *    `cleanupFadeDirectTimes`·`updateTabNotesInheritance`를 호출해 `notes`에 쓴다
 *    — "스크롤이 데이터를 편집하는" 결함이다(감사 B-4). 여기서는 노트를 **읽기만**
 *    한다. 쓰기는 1-3 입력 단계에서 명시적인 사용자 조작에만 붙인다.
 * 2. **`document`/`window`를 모듈 로드 시점에 캡처하지 않는다.** 팝아웃 창
 *    (`script.js`의 `popout-lists`)이 `#sidebar` 노드를 다른 document로 옮기고
 *    `getElementById`를 몽키패치하므로, 캡처해 두면 분리 후 엉뚱한 문서를 가리킨다.
 *    → 항상 컨테이너에서 `ownerDocument`를 되짚는다.
 * 3. **뷰포트 높이는 `window.innerHeight`가 아니라 컨테이너 `clientHeight`.**
 *    팝아웃 창에서는 메인 window 높이가 무의미하다. `ResizeObserver`를 쓰므로
 *    기존 노트 리스트의 "팝업 리사이즈를 못 받는" 한계도 함께 피한다.
 */

import {
    buildTimelineLayout,
    beatToRow,
    sectionStartBeats,
    rowLabelParts,
    buildEventPlacements,
    eventTooltipText
} from "./timeline-model.js";

/** 행 단위 프리셋 — 툴바의 "행 단위" 선택지. 값은 기준 subdivisions 대비 배수다. */
export const ROW_UNITS = [
    // 🔴 **1박보다 작은 단위가 필요하다.** 대형 차트를 1마디/행으로 보면 한 행 안에
    // 여러 노트가 들어가 줌을 올려도 **같은 행에 겹쳐** 개별로 안 보인다(사용자 보고).
    // 행 단위를 쪼개면 노트가 서로 다른 행으로 갈라진다 — 줌(행 높이)과는 다른 축이다.
    { label: "1/4박", beatsPerRowFactor: 0.25 },
    { label: "1/2박", beatsPerRowFactor: 0.5 },
    { label: "1박", beatsPerRowFactor: 1 },
    { label: "1마디 (4박)", beatsPerRowFactor: 4 }
];

/** 노트 타입 → 열. 설계 §1-2의 "노트 종류별 열". */
// export하는 이유: script.js의 좌우 드래그(TIMELINE_TYPE_COLUMNS)가 **같은 순서**를
// 전제한다. 둘이 갈리면 노트를 오른쪽으로 끌었을 때 엉뚱한 타입이 된다.
// 테스트가 두 목록의 일치를 검사한다(timeline-render.test.js).
export const COLUMNS = [
    { key: "tab", label: "Tab", types: ["tab", "longtab"] },
    { key: "direction", label: "Dir", types: ["direction", "longdirection"] },
    { key: "both", label: "Both", types: ["both", "longboth"] },
    { key: "node", label: "Node", types: ["node"] }
];

/**
 * 이벤트 열의 `data-column` 값.
 *
 * 🔴 **`COLUMNS`에 넣지 않는다.** 그 배열은 **노트 타입 ↔ 열**의 매핑이고
 * `script.js`의 `TIMELINE_TYPE_COLUMNS`와 1:1로 짝지어져 있다(테스트가 잠근다).
 * 여기에 이벤트를 끼우면 좌우 드래그가 노트를 "이벤트 타입"으로 바꾸려 들고,
 * 열 인덱스가 밀려 Node 열이 사라진다. 이벤트는 **노트 열 오른쪽의 별도 열**이며
 * 타입 매핑에 참여하지 않는다(합의된 배치).
 */
export const EVENT_COLUMN_KEY = "event";

const DEFAULT_ROW_HEIGHT = 24;   // px. 타임라인은 행 높이가 진짜 고정이다(§1-5).
const DEFAULT_OVERSCAN = 8;      // 뷰포트 위아래 여유 행

/** 타입 → 열 인덱스. 미지의 타입은 Tab 열로 떨어뜨린다(표시 누락 방지). */
function columnIndexForType(type) {
    const i = COLUMNS.findIndex(c => c.types.includes(type));
    return i < 0 ? 0 : i;
}

/**
 * 롱노트가 차지하는 행 수. `longTime`은 **박자 단위**다(내부 shape 기준,
 * `CONTRACT-NOTES.md §2`). JSON의 초 단위와 혼동하면 길이가 엉뚱해진다.
 */
function longSpanRows(note, baseSubdivisions, beatsPerRow) {
    if (!note.isLong || !note.longTime || note.longTime <= 0) return 0;
    const noteSubs = note.subdivisions || baseSubdivisions;
    const normalized = note.longTime * (baseSubdivisions / noteSubs);
    return normalized / beatsPerRow;
}

/**
 * 타임라인 뷰의 상태. **뷰 전용**이다 — 차트 데이터가 아니므로 저장되지 않는다.
 *
 * `snapBeats`를 전역 `subdivisions`와 분리해 두는 이유(§1-2-C): 전역값을 바꾸면
 * 기존 노트의 시간이 재해석된다(`updateNotesForTimeBasedChange`). 스냅은 격자만
 * 바꾸는 뷰 상태여야 안전하다.
 */
export function createTimelineState() {
    return {
        // 기본 1마디. 🔴 ROW_UNITS에 1/4박·1/2박을 앞에 추가했으므로 **인덱스가 3**이다
        // (이 값을 1로 두면 기본이 1/2박이 되어 대형 차트에서 행 수가 8배로 늘어난다).
        rowUnitIndex: 3,
        rowHeight: DEFAULT_ROW_HEIGHT,
        scrollTop: 0,
        overscan: DEFAULT_OVERSCAN,
        // 스냅 격자의 분모. 4 = 1/4(4분음표) — 기준 subdivisions를 이 값으로 나눈 만큼이
        // 한 칸이다. 툴바에서 바꾼다.
        snapDivisor: 4
    };
}

/** 현재 행 단위가 담는 기준 박자 수. */
export function beatsPerRowOf(state, baseSubdivisions) {
    const unit = ROW_UNITS[state.rowUnitIndex] || ROW_UNITS[0];
    return baseSubdivisions * unit.beatsPerRowFactor;
}

/**
 * 그릴 행 범위를 정한다. 가상 스크롤의 전부다.
 *
 * `viewportHeight`는 **컨테이너의 `clientHeight`**여야 한다(§1-6).
 * 0이면(탭이 숨겨져 있어 측정 불가) 최소 1행을 그려 빈 화면을 피한다.
 */
export function visibleRowRange(state, rowCount, viewportHeight) {
    const rowHeight = state.rowHeight || DEFAULT_ROW_HEIGHT;
    const visible = viewportHeight > 0 ? Math.ceil(viewportHeight / rowHeight) : 1;

    const first = Math.max(0, Math.floor(state.scrollTop / rowHeight) - state.overscan);
    const last = Math.min(rowCount - 1, first + visible + state.overscan * 2);
    return { first, last };
}

/**
 * 행 → 그 행에 걸리는 placement 목록. 렌더가 매 행마다 전체를 훑지 않게 한다.
 *
 * 롱노트는 여러 행에 걸치므로 시작 행뿐 아니라 지나가는 행에도 등록한다
 * (`isHead`로 구분해 헤드만 마커를 그린다).
 *
 * **placements를 수정하지 않는다** — 새 Map만 만든다.
 */
export function indexPlacementsByRow(placements, notes, baseSubdivisions, beatsPerRow) {
    const byRow = new Map();

    const push = (row, entry) => {
        if (!byRow.has(row)) byRow.set(row, []);
        byRow.get(row).push(entry);
    };

    for (const p of placements) {
        const note = notes[p.index];
        const span = longSpanRows(note, baseSubdivisions, beatsPerRow);

        // 롱노트의 끝이 어느 행 어디인지. 길이 조절 손잡이를 그 자리에 놓는다.
        const endBeat = p.absoluteBeat + span * beatsPerRow;
        const endRow = span > 0 ? beatToRow(endBeat, beatsPerRow) : p.row;
        const endFraction = span > 0
            ? (endBeat - endRow * beatsPerRow) / beatsPerRow
            : p.rowFraction;

        // 헤드 행이 곧 끝 행이면(한 행 안에 들어가는 짧은 롱노트) 헤드가 손잡이도 갖는다.
        push(p.row, { ...p, isHead: true, isTailEnd: span > 0 && endRow === p.row, endFraction });

        if (span <= 0) continue;

        for (let r = p.row + 1; r <= endRow; r++) {
            push(r, { ...p, isHead: false, isTailEnd: r === endRow, endFraction });
        }
    }

    return byRow;
}

/**
 * 타임라인을 그린다.
 *
 * @param {HTMLElement} container `#timeline-grid` — 스크롤되는 요소
 * @param {object} ctx `{ notes, bpm, subdivisions, selectedIndex, state }`
 *
 * 노트 배열은 **읽기만** 한다.
 */
export function renderTimeline(container, ctx) {
    if (!container) return;

    const { notes, bpm, subdivisions, state } = ctx;
    const beatsPerRow = beatsPerRowOf(state, subdivisions);

    const { rowCount, placements } = buildTimelineLayout(notes, bpm, subdivisions, beatsPerRow);
    const byRow = indexPlacementsByRow(placements, notes, subdivisions, beatsPerRow);

    // 구간 시작점. 구간이 여럿이면 행 라벨을 `구간:지역박자`로 낸다 —
    // beatReset 뒤에서는 절대 박자가 표의 Beat와 어긋나기 때문이다.
    const starts = sectionStartBeats(notes, bpm, subdivisions);

    // 이벤트 열. **이벤트는 초(preDelay 후) 축**이라 박자로 환산해 얹는다
    // (buildEventPlacements 주석 참조). 이벤트가 없으면 빈 Map이라 비용이 없다.
    const eventsByRow = new Map();
    for (const group of buildEventPlacements(
        ctx.events || [], ctx.preDelaySeconds || 0, bpm, subdivisions, beatsPerRow
    )) {
        if (!eventsByRow.has(group.row)) eventsByRow.set(group.row, []);
        eventsByRow.get(group.row).push(group);
    }

    // 팝아웃 대비: 매번 컨테이너가 실제로 속한 문서를 되짚는다(§1-6).
    const doc = container.ownerDocument;
    const rowHeight = state.rowHeight || DEFAULT_ROW_HEIGHT;

    // 뷰포트는 컨테이너 기준. window.innerHeight를 쓰면 팝업에서 어긋난다.
    const { first, last } = visibleRowRange(state, rowCount, container.clientHeight);

    // 스페이서 2개 사이에 보이는 행만 넣는 방식 — 기존 노트 리스트와 같은 관행.
    const frag = doc.createDocumentFragment();

    const topSpacer = doc.createElement("div");
    topSpacer.className = "timeline-spacer";
    topSpacer.style.height = `${first * rowHeight}px`;
    frag.appendChild(topSpacer);

    // 마지막 콘텐츠 이후의 행은 옅게 깐다. 대형 차트는 종료 노트가 콘텐츠보다 훨씬
    // 뒤에 있어(실측: 콘텐츠 1917박 vs 종료 3950박) 그 사이가 통째로 빈 격자다 —
    // 표시가 없으면 "노트가 안 그려졌나?"로 읽힌다. **데이터는 건드리지 않는다.**
    const contentEndRow = ctx.contentEndBeat !== undefined
        ? beatToRow(ctx.contentEndBeat, beatsPerRow)
        : Infinity;

    for (let row = first; row <= last; row++) {
        const rowEl = buildRowElement(
            doc, row, byRow.get(row) || [], ctx, beatsPerRow, rowHeight, starts,
            eventsByRow.get(row) || []
        );
        if (row > contentEndRow) rowEl.classList.add("timeline-row-past-content");
        frag.appendChild(rowEl);
    }

    const bottomSpacer = doc.createElement("div");
    bottomSpacer.className = "timeline-spacer";
    bottomSpacer.style.height = `${Math.max(0, (rowCount - 1 - last) * rowHeight)}px`;
    frag.appendChild(bottomSpacer);

    // 오버레이(재생 헤드·인스펙터)는 행이 아니라 컨테이너에 겹쳐 있다.
    // replaceChildren이 통째로 지우면 재생 헤드는 매 프레임 깜빡이고, 인스펙터는
    // 값을 고치는 순간(renderNoteList → 재렌더) 닫혀 버린다 — 살려서 되돌린다.
    const overlays = [
        container.querySelector(".timeline-playhead"),
        container.querySelector("#timeline-inspector"),
        container.querySelector(".timeline-end-handle")
    ].filter(Boolean);

    container.replaceChildren(frag);
    for (const el of overlays) container.appendChild(el);
}

/** 한 행의 DOM. 격자 셀 + 그 행에 걸린 노트 마커 + 이벤트 마커. */
function buildRowElement(doc, row, entries, ctx, beatsPerRow, rowHeight, starts, eventGroups = []) {
    const { notes, selectedIndex } = ctx;

    const rowEl = doc.createElement("div");
    rowEl.className = "timeline-row";
    rowEl.style.height = `${rowHeight}px`;
    rowEl.dataset.row = String(row);

    // 라벨은 구간을 반영한다. 구간이 하나면 지금까지처럼 절대 박자 하나만 낸다.
    const rowBeat = row * beatsPerRow;

    // 마디 첫 행을 굵게 — 박자 감각의 기준선.
    // 🔴 `row % 4`로 판정하면 안 된다. 행 단위가 1마디일 때만 맞고, 1/4박 단위에서는
    // 4행마다(=1박) 굵어져 마디 감각이 사라진다. **실제 박자**로 판정한다.
    const beatsPerMeasure = (ctx.subdivisions || 16) * 4;
    if (rowBeat % beatsPerMeasure === 0) rowEl.classList.add("timeline-row-strong");
    const parts = rowLabelParts(rowBeat, starts);

    const label = doc.createElement("div");
    label.className = "timeline-row-label";
    label.textContent = parts.text;
    if (parts.isSectionStart && parts.sectionIndex > 0) {
        // 구간이 시작하는 행은 라벨을 강조해 경계를 눈에 띄게 한다.
        label.classList.add("timeline-row-label-section");
        label.title = `구간 ${parts.sectionIndex} 시작 (절대 ${rowBeat}박)`;
    } else if (starts && starts.length > 1) {
        label.title = `구간 ${parts.sectionIndex} · 지역 ${parts.localBeat}박 (절대 ${rowBeat}박)`;
    }
    rowEl.appendChild(label);

    // 빈 셀도 항상 만든다 — 1-3의 격자 클릭 삽입이 이 셀을 과녁으로 쓴다.
    const cells = COLUMNS.map(col => {
        const cell = doc.createElement("div");
        cell.className = "timeline-cell";
        cell.dataset.column = col.key;
        cell.dataset.row = String(row);
        rowEl.appendChild(cell);
        return cell;
    });

    // 이벤트 열 — 노트 4열의 **오른쪽 5번째**(합의된 배치). 노트 열에 겹치지 않는다.
    //
    // 🔴 `cells`에 넣지 않는다. 그 배열은 인덱스가 곧 노트 타입이라
    // (`columnIndexForType`) 이벤트 셀이 섞이면 타입 매핑이 밀린다.
    // `script.js`의 열 판정도 `.timeline-cell[data-column]`을 노트 열로만 훑는다.
    const eventCell = doc.createElement("div");
    eventCell.className = "timeline-cell timeline-event-cell";
    eventCell.dataset.column = EVENT_COLUMN_KEY;
    eventCell.dataset.row = String(row);
    rowEl.appendChild(eventCell);

    for (const group of eventGroups) {
        const marker = doc.createElement("div");
        marker.className = "timeline-event";
        marker.style.top = `${group.rowFraction * 100}%`;

        // 인덱스 목록을 통째로 실어 둔다 — 클릭(목록 팝업)·드래그(시간 이동)가
        // 그룹 단위로 동작해야 하기 때문이다. 개별 분리는 팝업이 맡는다.
        marker.dataset.eventIndices = group.indices.join(",");
        marker.title = eventTooltipText(group);

        // 같은 시각의 이벤트는 마커 **하나를 공유**하고 개수를 적는다(합의).
        // 실측 차트가 3쌍이나 겹쳐 있어 개수 표시가 실제로 필요하다.
        //
        // 숫자를 **별도 span**에 넣는 이유: 마커가 45도 회전해 있어 글자를 그대로
        // 두면 기울어져 읽히지 않는다. CSS가 이 span만 역회전시킨다.
        if (group.events.length > 1) {
            marker.classList.add("grouped");
            const count = doc.createElement("span");
            count.className = "timeline-event-count";
            count.textContent = String(group.events.length);
            marker.appendChild(count);
        }

        // preDelay보다 이른 이벤트 — 0행에 끌어올린 것이라 실제 위치가 아니다.
        // 표시가 없으면 "0행에 있는 이벤트"로 오인된다.
        if (group.clamped) marker.classList.add("before-start");

        eventCell.appendChild(marker);
    }

    for (const entry of entries) {
        const note = notes[entry.index];
        if (!note) continue;

        const cell = cells[columnIndexForType(note.type)];
        const marker = doc.createElement("div");

        marker.className = entry.isHead ? "timeline-note" : "timeline-note-tail";
        marker.dataset.noteIndex = String(entry.index);
        if (entry.index === selectedIndex) marker.classList.add("selected");

        if (entry.isHead) {
            // 행 안에서의 세로 위치. 행 단위보다 촘촘한 노트도 제자리에 찍힌다.
            marker.style.top = `${entry.rowFraction * 100}%`;
            marker.title = `#${entry.index} ${note.type} beat=${note.beat}`;

            // beatReset은 **구간 경계**다. 이 노트 이후로 beat가 0부터 다시 세어지므로
            // 표시가 없으면 좌측 라벨과 표의 Beat가 왜 어긋나는지 알 수 없다.
            // 행 전체에도 경계선을 그어 어디서 끊겼는지 한눈에 보이게 한다.
            if (note.beatReset) {
                marker.classList.add("beat-reset");
                marker.title += " · beatReset(구간 시작)";
                rowEl.classList.add("timeline-row-reset");
            }

            // 롱노트는 헤드(가로 바) + 꼬리(좁은 세로 막대)가 "T"를 이룬다.
            // 헤드가 있는 행에도 꼬리가 필요하다 — 없으면 첫 행만 막대 없이 뜬다.
            // 다음 행 이후의 꼬리는 별도 entry(isHead=false)가 그린다.
            //
            // 길이는 이 행에 남은 공간(1 - rowFraction)과 실제 남은 길이(span) 중
            // 짧은 쪽이다. **px로 준다** — 마커 자체가 6px짜리라 %를 쓰면 그 6px에
            // 걸려 꼬리가 거의 안 보인다.
            const span = longSpanRows(note, ctx.subdivisions, beatsPerRow);
            if (span > 0) {
                marker.classList.add("has-tail");
                const withinRow = Math.min(1 - entry.rowFraction, span);
                marker.style.setProperty("--tail-height", `${withinRow * rowHeight}px`);
            }
        } else {
            // 이어지는 꼬리. 마지막 행에서는 노트가 끝나는 지점까지만 그린다 —
            // 행 끝까지 그리면 길이가 실제보다 길어 보인다.
            if (entry.isTailEnd) {
                marker.style.bottom = "auto";
                marker.style.height = `${entry.endFraction * rowHeight}px`;
            }
        }

        // 길이 조절 손잡이 — 꼬리 끝에 둔다. 이것을 끌면 longTime이 바뀐다.
        // 별도 엘리먼트인 이유: 헤드의 꼬리는 ::after 가상 요소라 클릭을 못 받는다.
        if (entry.isTailEnd) {
            const grip = doc.createElement("div");
            grip.className = "timeline-long-grip";
            grip.dataset.noteIndex = String(entry.index);
            grip.title = "드래그해서 롱노트 길이 조절";
            grip.style.top = `${entry.endFraction * rowHeight}px`;
            cell.appendChild(grip);
        }

        cell.appendChild(marker);
    }

    return rowEl;
}

/**
 * 재생 헤드 위치(px). 재생 중 오디오 시간을 행 좌표로 옮긴다.
 *
 * `preDelaySeconds`를 빼는 이유: 에디터가 다루는 값은 preDelay 적용 **전** 시간
 * (`originalTime`)인데(`script.js`의 signature 주석), 오디오 재생 시간은 적용 후다.
 * 빼지 않으면 헤드가 preDelay만큼 앞서 달린다.
 */
export function playheadOffsetPx(currentTimeSeconds, preDelaySeconds, bpm, subdivisions, state) {
    const beatsPerRow = beatsPerRowOf(state, subdivisions);
    const rowHeight = state.rowHeight || DEFAULT_ROW_HEIGHT;

    const originalTime = currentTimeSeconds - preDelaySeconds;
    const absoluteBeat = (originalTime * bpm * subdivisions) / 60;

    return (absoluteBeat / beatsPerRow) * rowHeight;
}

/**
 * 재생 헤드를 화면 안에 유지하기 위한 scrollTop. 추종 스크롤(병목 5).
 *
 * 헤드가 뷰포트 안에 있으면 **현재 스크롤을 그대로 둔다** — 매 프레임 재중앙화하면
 * 화면이 계속 흔들려 작업이 어렵다.
 */
export function followScrollTop(playheadPx, viewportHeight, currentScrollTop) {
    if (viewportHeight <= 0) return currentScrollTop;

    const margin = viewportHeight * 0.2;
    const top = currentScrollTop + margin;
    const bottom = currentScrollTop + viewportHeight - margin;

    if (playheadPx >= top && playheadPx <= bottom) return currentScrollTop;
    return Math.max(0, playheadPx - viewportHeight / 2);
}
